import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Box, CircularProgress, ThemeProvider, Typography, createTheme } from '@mui/material';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/authContext';
import { Layout } from './components/layout';
import { LoginPage } from './pages/login';
import { TeamWorkForm } from './pages/workForm';
import { ProtectedRoute } from './components/protectedRoute';
import { Dashboard } from './pages/dashboard';
import TimesheetList from './pages/timesheets';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { store, persistor } from './redux/index';
import { initNetworkListener } from './services/monitorConnectivity';
import { UnauthorizedPage } from './pages/unauthorized';
import { UserRoleProvider, useUserRole } from './context/userRoleContext';
import { TeamManagement } from './pages/teamManagement';
import { ExportView } from './pages/export';
import config from './services/config';
import { useEffect } from 'react';

const theme = createTheme({
  palette: {
    primary: {
      main: config.app.mainColor,
    },
    secondary: {
      main: config.app.secondaryColor, // Purple or another complementary color
      contrastText: '#fff', // White text for contrast
    },
    success: {
      main: '#388E3C', // Green
      contrastText: '#fff', // White text for contrast
    },
    warning: {
      main: '#F57C00', // Orange
      contrastText: '#fff', // White text for contrast
    },
    error: {
      main: '#bc1e1c', // Optional: deeper red for errors
      contrastText: '#fff', // White text for contrast
    },
  },
  typography: {
    fontFamily: 'Noto Sans',
  },
});

// Initialize network listeners
initNetworkListener();

const DefaultRedirect = () => {
  const { isAdmin, isLoading: roleLoading } = useUserRole();
  const { isAuthenticated, isLoading } = useAuth();

  // Show loading state while either auth or role data is loading
  if (isLoading || roleLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh'
        }}
      >
        <CircularProgress size={60} thickness={4} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading...
        </Typography>
      </Box>
    );
  }

  // Only redirect to login if we're sure authentication has failed
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  // At this point we know user is authenticated and role is loaded
  return <Navigate to={isAdmin ? "/dashboard" : "/timesheetList"} replace />;
};


function App() {
  useEffect(() => {
    if (config.app.title) {
      document.title = config.app.title;
    }
  }, []);

  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <BrowserRouter>
          <AuthProvider>
            <UserRoleProvider>
              <ThemeProvider theme={theme}>
                <Toaster position="top-right" />
                <Routes>
                  <Route path="/login" element={<LoginPage />} />

                  <Route path="/dashboard" element=
                    {<ProtectedRoute requiredRole={["Admin","HR"]}>
                      <Layout>
                        <Dashboard />
                      </Layout>
                    </ProtectedRoute>
                    } />
                  <Route path="/timesheet" element={
                    <ProtectedRoute requiredRole={["User","HR","Foreman"]}>
                      <Layout>
                        <TeamWorkForm />
                      </Layout>
                    </ProtectedRoute>
                  } />

                  <Route path="/timesheetList" element={
                    <ProtectedRoute>
                      <Layout>
                        <TimesheetList />
                      </Layout>
                    </ProtectedRoute>
                  }
                  />

                  <Route path="/teams" element={
                    <ProtectedRoute requiredRole={["User","HR","Foreman"]}>
                      <Layout>
                        <TeamManagement />
                      </Layout>
                    </ProtectedRoute>
                  }
                  />

                  <Route path="/export" element={
                    <ProtectedRoute requiredRole={["HR"]}>
                      <Layout>
                        <ExportView />
                      </Layout>
                    </ProtectedRoute>
                  } />
                  <Route path="/unauthorized" element={<UnauthorizedPage />} />
                  <Route path="/" element={<DefaultRedirect />} />
                  <Route path="*" element={<DefaultRedirect />} />
                </Routes>
              </ThemeProvider>
            </UserRoleProvider>
          </AuthProvider>
        </BrowserRouter>
      </PersistGate>
    </Provider>
  );
}

export default App;
